/**
 * Renders welcome-pack plain text into blog-style blocks (headings, lists, paragraphs).
 */

const BULLET_RE = /^[·•\-]\s*(.+)$/

function isBulletLine(line) {
  return BULLET_RE.test(line.trim())
}

function bulletContent(line) {
  return line.trim().replace(BULLET_RE, '$1')
}

function isSectionHeading(line) {
  const t = line.trim()
  if (t.length < 4 || t.length > 72) return false
  const letters = t.replace(/[^a-zA-Z]/g, '')
  if (letters.length < 4) return false
  return t === t.toUpperCase()
}

function renderChunks(text, editorial) {
  const chunks = text.split(/\n\n+/).map((c) => c.trim()).filter(Boolean)
  const elements = []

  const sectionHeadingClass = editorial
    ? 'text-left font-bold uppercase tracking-[0.08em] text-black text-[0.8125rem] sm:text-sm'
    : 'text-xs font-semibold uppercase tracking-[0.2em] text-black/50'

  const bodyClass = editorial
    ? 'text-left text-[1.0625rem] sm:text-[1.0625rem] leading-[1.85] text-black'
    : 'my-5 text-[17px] leading-[1.85] text-black/80'

  const listItemClass = editorial
    ? 'flex gap-3 text-[1.0625rem] leading-[1.75] text-black pl-0.5'
    : 'flex gap-3 text-[17px] leading-[1.7] text-black/80'

  const listGap = editorial ? 'space-y-5 sm:space-y-6' : 'space-y-3'

  chunks.forEach((chunk, idx) => {
    const lines = chunk.split('\n').map((l) => l.trim()).filter(Boolean)
    if (lines.length === 0) return

    const key = `chunk-${idx}`

    if (lines.every(isBulletLine)) {
      elements.push(
        <ul key={key} className={`list-none pl-0 ${editorial ? listGap : 'my-6 space-y-3'}`}>
          {lines.map((line, i) => (
            <li key={i} className={listItemClass}>
              <span className="mt-[0.6em] h-1 w-1 flex-shrink-0 rounded-full bg-black" aria-hidden />
              <span>{bulletContent(line)}</span>
            </li>
          ))}
        </ul>
      )
      return
    }

    if (lines.length > 1 && !isBulletLine(lines[0]) && lines.slice(1).every(isBulletLine)) {
      elements.push(
        <div key={key} className={editorial ? 'space-y-6' : 'mt-10 first:mt-0 space-y-4'}>
          <h2 className={sectionHeadingClass}>{lines[0]}</h2>
          <ul className={`list-none pl-0 ${listGap}`}>
            {lines.slice(1).map((line, i) => (
              <li key={i} className={listItemClass}>
                <span className="mt-[0.6em] h-1 w-1 flex-shrink-0 rounded-full bg-black" aria-hidden />
                <span>{bulletContent(line)}</span>
              </li>
            ))}
          </ul>
        </div>
      )
      return
    }

    if (lines.length > 1 && isSectionHeading(lines[0]) && !lines.slice(1).some(isBulletLine)) {
      elements.push(
        <div key={key} className={editorial ? 'space-y-6' : 'mt-10 space-y-4 first:mt-0'}>
          <h2 className={sectionHeadingClass}>{lines[0]}</h2>
          <p className={`${bodyClass} ${editorial ? 'm-0' : ''}`}>{lines.slice(1).join(' ')}</p>
        </div>
      )
      return
    }

    if (lines.length === 1) {
      const line = lines[0]
      if (isSectionHeading(line)) {
        const isBrandKicker = line.length <= 24 && line.split(/\s+/).length <= 3
        if (editorial && isBrandKicker) {
          elements.push(
            <p
              key={key}
              className="text-center text-[11px] font-semibold uppercase tracking-[0.35em] text-black first:mt-0 sm:text-xs"
            >
              {line}
            </p>
          )
          return
        }
        if (editorial) {
          elements.push(
            <h2 key={key} className={`${sectionHeadingClass} ${editorial ? 'mt-0' : ''}`}>
              {line}
            </h2>
          )
          return
        }
        elements.push(
          isBrandKicker ? (
            <p
              key={key}
              className="mt-6 text-center text-[11px] font-semibold uppercase tracking-[0.35em] text-black/45 first:mt-0 sm:text-xs"
            >
              {line}
            </p>
          ) : (
            <h2 key={key} className="mt-10 text-xs font-semibold uppercase tracking-[0.2em] text-black/50 first:mt-0">
              {line}
            </h2>
          )
        )
        return
      }
      if (!editorial && idx === 0 && line.length < 120 && !line.endsWith('.')) {
        elements.push(
          <p
            key={key}
            className="text-xl font-medium leading-snug text-black sm:text-2xl border-l-[3px] border-black pl-5 py-1 my-6 first:mt-0"
          >
            {line}
          </p>
        )
        return
      }
      const isIntro = editorial && idx === 0 && line.endsWith('.')
      elements.push(
        <p
          key={key}
          className={
            isIntro
              ? 'm-0 text-left text-lg sm:text-[1.125rem] leading-[1.85] text-black'
              : `${bodyClass} ${editorial ? 'm-0' : 'first:mt-0'}`
          }
        >
          {line}
        </p>
      )
      return
    }

    elements.push(
      <p key={key} className={`${bodyClass} ${editorial ? 'm-0' : ''}`}>
        {lines.join(' ')}
      </p>
    )
  })

  return elements
}

const ReserveWelcomePackArticleBody = ({ text, editorial = false }) => {
  if (!text) {
    return <p className="text-black/45 italic m-0 text-center sm:text-left">Content for this section will be added here.</p>
  }
  const wrapperClass = editorial
    ? 'reserve-welcome-pack-article flex flex-col gap-12 sm:gap-16 text-left'
    : 'reserve-welcome-pack-article'
  return <div className={wrapperClass}>{renderChunks(text, editorial)}</div>
}

export default ReserveWelcomePackArticleBody
